// include=animation` is asked for ONLY when Enhanced Visuals is on: BE-Light measured the frames at
// +43% on a lean field's manifest, on a call every consumer makes every time ANY map opens, so a
// user who switched the animation off must not pay for it forever. There is deliberately NO
// server-side check of the toggle — that would put a second interpreter of "absent means ON" in the
// system — so this is the only gate and it has to hold.
import { useEffect, useState } from "react";
import { api } from "../api";
import { cellsFromFrameSource } from "../lib/animFrames";
import { bboxOfGeoJSON, isFittableBbox, type Bbox } from "../lib/geo";
import type { AnimCell } from "../lib/mapSoilAnimation";

/*
 *
 * `loading` while the manifest is in flight, `ready` once there is something to draw, `empty` when
 * there is nothing and never will be. THE STATUS IS NOT COSMETIC: the caller waits for `ready`
 * before starting its clock, so the three seconds are three seconds of ANIMATION rather than of an
 * empty box — the bug that produced the blank rectangle reported.
 */
export type AnimStatus = "loading" | "ready" | "empty";

export function useAnimCells(fieldId: string | undefined, enabled: boolean) {
  const [state, setState] = useState<{
    cells: AnimCell[];
    labels: string[];
    keys: string[];
    bitLabels: (string[] | null)[];
    ramps: (string[] | null)[];
    stale: boolean;
    status: AnimStatus;
    aspect: number;
    bbox: Bbox | null;
  }>({
    cells: [],
    labels: [],
    // the SERVED plane keys, so an animation can ask for a layer BY NAME rather than by position
    keys: [],
    bitLabels: [],
    ramps: [],
    /*
     *
     * BOUNDED, NOT UNBOUNDED: the planes are written INSIDE `run_autorx`, so a plane set cannot
     * drift and stay drifted while the Rx moves on — the window is the gap between the property
     * runs and `autorx` in ONE plan run. Honoured anyway: it costs a boolean, and the case it does
     * cover (autorx failing after the rasters were rebuilt) is real.
     */
    stale: false,
    status: "loading",
    /*
     *
     * The preview and the animation now measure the field the SAME way, which is the only way the
     * two pictures can agree.
     */
    bbox: null,
    // Compute's raster is aspect-matched — computed on UTM bounds in METRES, so there is no cos-lat
    // error at source — and the renderer letterboxes to it: a long thin field squeezed into a fixed
    // box animates as a shape the grower does not recognise.
    aspect: 1,
  });

  useEffect(() => {
    if (!enabled || !fieldId) return;
    let alive = true;
    setState((v) => ({ ...v, status: "loading" }));
    void Promise.all([
      api.fetchFieldLayerList(fieldId),
      api.fetchLayerDetail(fieldId, "animation-frames").catch(() => null),
    ])
      .then(async ([listed, row]) => {
        if (!alive) return;
        // A missing row, frames that do not parse, and a signed URL that does
        // not load are the same outcome: nothing to draw.
        const got = await cellsFromFrameSource(row);
        if (!alive) return;
        if (!got) {
          console.info("[anim] no frame set for this field");
          setState((v) => ({
            ...v,
            cells: [],
            labels: [],
            keys: [],
            bitLabels: [],
            ramps: [],
            stale: false,
            status: "empty",
          }));
          return;
        }
        // 17–22 planes per field is NORMAL (20 layers, bare soil expanding to 3, minus any the field
        // genuinely has no data for — no samples means no diffusion water columns, so Kc/Ks/ET drop).
        // That is the shorter-loop contract working, not an error.
        console.info(
          "[anim] served frames · %d frames · %d cells",
          got.labels.length,
          got.cells.length,
        );
        setState({
          cells: got.cells,
          labels: got.labels,
          keys: got.keys,
          bitLabels: got.bitLabels,
          ramps: got.ramps,
          stale: (row as { is_stale?: boolean } | undefined)?.is_stale === true,
          status: "ready",
          aspect: got.aspect,
          bbox: null,
        });

        // Resolved after the pixels are already on screen because it is only needed at act two — 5
        // s later — and must never delay the animation. Excludes the `automap-*` vector rows
        // exactly as PreviewMap does, or the fallback picks one of THEM for the boundary and we are
        // back to measuring the field by the wrong thing.
        if (!alive) return;
        const bb = bboxOfGeoJSON(
          listed.document?.boundary as GeoJSON.GeoJSON | undefined,
        );
        if (!isFittableBbox(bb)) {
          console.info("[anim] no boundary bbox — points will not be drawn");
          return;
        }
        setState((v) => ({ ...v, bbox: bb }));
      })
      .catch((e: unknown) => {
        if (!alive) return;
        console.info(
          "[anim] manifest read failed — %s",
          e instanceof Error ? e.message : String(e),
        );
        setState((v) => ({
          ...v,
          cells: [],
          labels: [],
          keys: [],
          bitLabels: [],
          ramps: [],
          stale: false,
          status: "empty",
        }));
      });
    return () => {
      alive = false;
    };
  }, [fieldId, enabled]);

  return state;
}
